/**
 * Page metadata. React 19 hoists <title>, <meta> and <link> rendered anywhere into <head>,
 * so a page renders `<Seo …/>` once, near its top.
 *
 * - `title` is the page name; « · Cardnd » is appended (except on the home page, `exactTitle`).
 * - `path` is the canonical path (no query string); the origin is the production site.
 * - `noindex` for private pages (account, checkout, confirmations, auth) and error states.
 * - `image`: the share image; the site's default one (`/og-cardnd.jpg`, 1200×630) otherwise.
 * - `description`: the site's own (`ns.common` `seo.siteDescription`) when the page has none.
 * - `jsonLd`: one schema.org object or a list.
 * - Language (P12, Decision 95): titles and descriptions are whatever the page passes, in the current language;
 *   the canonical URL is the same in every language (one URL per page); `og:locale` follows. Crawlers have no
 *   stored choice, so they get French. Only the city landing pages have an English URL (Decision 112): they pass
 *   `alternates` (both versions), rendered as reciprocal `hreflang` links with `x-default` = French.
 * - Static head (Decision 109): `index.html` and the prerendered pages (`scripts/prerender.mjs`) carry head tags
 *   marked `data-static-head` for crawlers and link previews that run no JavaScript. The first `<Seo>` removes them,
 *   so the document never holds two titles, descriptions or canonicals: React's tags are the only ones left.
 */
import { useLayoutEffect } from 'react';
import { ns, useLocale, useT, type Locale } from '@/i18n';
import { DEFAULT_SHARE_IMAGE, removeStaticHead } from './staticHead';

const ORIGIN = 'https://cardnd.com';

interface SeoProps {
  title: string;
  description?: string;
  path?: string;
  image?: string;
  noindex?: boolean;
  exactTitle?: boolean;
  jsonLd?: object | object[];
  /** The page's URL in each language (`hreflang`), only for pages that have both. */
  alternates?: Record<Locale, string>;
}

export function Seo({ title, description, path, image, noindex, exactTitle, jsonLd, alternates }: SeoProps) {
  const locale = useLocale();
  const tc = useT(ns.common);
  const text = description || tc('seo.siteDescription');
  const fullTitle = exactTitle ? title : `${title} · Cardnd`;
  const url = path ? `${ORIGIN}${path}` : undefined;
  const shareImage = image || DEFAULT_SHARE_IMAGE;
  useLayoutEffect(() => {
    if (typeof document !== 'undefined') removeStaticHead();
  }, []);
  return (
    <>
      <title>{fullTitle}</title>
      <meta name="description" content={text} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {url && <link rel="canonical" href={url} />}
      {alternates && (
        <>
          <link rel="alternate" hrefLang="fr" href={`${ORIGIN}${alternates.fr}`} />
          <link rel="alternate" hrefLang="en" href={`${ORIGIN}${alternates.en}`} />
          <link rel="alternate" hrefLang="x-default" href={`${ORIGIN}${alternates.fr}`} />
        </>
      )}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={text} />
      {url && <meta property="og:url" content={url} />}
      <meta property="og:image" content={shareImage} />
      <meta property="og:site_name" content="Cardnd" />
      <meta property="og:locale" content={locale === 'en' ? 'en_GB' : 'fr_MA'} />
      <meta name="twitter:card" content="summary_large_image" />
      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
    </>
  );
}
